"use client"

import { useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"

import { trpc } from "@/lib/trpc/client"

import { TRPCProvider } from "@/components/providers/TRPCProvider"
import { PhoneField } from "@/components/ui"

import { SalesIcon } from "./icons"
import { Eyebrow, H2, Lede, Shell } from "./primitives"

// Substitui a OfferSection quando o curso está inativo (mode='waitlist'):
// mesmo slot narrativo (id="oferta", tone alt, card à direita), mas o card
// captura lista de espera em vez de vender.

interface WaitlistSectionProps {
  courseId: string
  isLoggedIn: boolean
}

const INPUT_CLASS =
  "w-full rounded-xl border border-sales-line bg-white px-4 py-3 text-[14.5px] text-sales-ink placeholder:text-sales-text-mid focus:border-primary-400 focus:ring-2 focus:ring-primary-200 focus:outline-none"

const BUTTON_CLASS =
  "inline-flex w-full items-center justify-center rounded-xl bg-primary-600 px-6 font-semibold tracking-[-0.01em] text-white shadow-[0_8px_22px_-10px_rgba(198,6,131,.65)] transition-transform active:scale-[0.98] disabled:opacity-60"

function SuccessCard() {
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
        <SalesIcon.shield width={26} height={26} className="text-primary-600" />
      </div>
      <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-sales-ink">
        Você está na lista!
      </p>
      <p className="max-w-[36ch] text-[14px] leading-[1.6] text-sales-text">
        Assim que as vagas abrirem, você será avisado(a) por email — antes de
        todo mundo.
      </p>
    </div>
  )
}

function WaitlistSectionInner({ courseId, isLoggedIn }: WaitlistSectionProps) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [phoneValid, setPhoneValid] = useState(false)
  const [website, setWebsite] = useState("") // honeypot

  // WhatsApp é opcional: só envia se válido (E.164), senão omite.
  const phoneToSend = phone && phoneValid ? phone : undefined
  const [joined, setJoined] = useState(false)

  const myStatus = useQuery(
    trpc.waitlist.myStatus.queryOptions(
      { courseId },
      { enabled: isLoggedIn, retry: false }
    )
  )

  const join = useMutation(
    trpc.waitlist.join.mutationOptions({
      onSuccess: () => setJoined(true),
    })
  )

  const onList = joined || myStatus.data?.onList

  function submitAnonymous(e: React.FormEvent) {
    e.preventDefault()
    if (join.isPending) return
    join.mutate({
      courseId,
      email,
      name: name || undefined,
      phone: phoneToSend,
      website: website || undefined,
    })
  }

  function submitLoggedIn() {
    if (join.isPending) return
    join.mutate({ courseId, phone: phoneToSend })
  }

  return (
    <Shell tone="alt" className="py-12 sm:py-18 lg:py-24">
      <div id="oferta" className="scroll-mt-[70px]" />
      <div className="lg:grid lg:grid-cols-[1fr_0.92fr] lg:items-start lg:gap-12">
        <div>
          <Eyebrow className="text-sales-text-mid">Lista de espera</Eyebrow>
          <H2 className="max-w-[20ch] text-sales-ink">
            Turmas fechadas no momento. Garanta seu lugar na próxima.
          </H2>
          <Lede className="text-sales-text">
            As matrículas estão temporariamente encerradas. Quem está na lista
            de espera é avisado por email em primeira mão quando uma nova turma
            abre — antes da divulgação pública.
          </Lede>
        </div>

        <div className="mt-9 rounded-3xl border border-sales-line bg-white p-6 shadow-[0_24px_60px_-34px_rgba(6,16,20,.22)] sm:p-8 lg:mt-0">
          {onList ? (
            <SuccessCard />
          ) : (
            <>
              <Eyebrow className="text-sales-text-mid">
                Avise-me quando abrir
              </Eyebrow>

              {isLoggedIn ? (
                <div className="mt-5 flex flex-col gap-3">
                  <PhoneField
                    value={phone}
                    onChange={(e164, meta) => {
                      setPhone(e164)
                      setPhoneValid(meta.isValid)
                    }}
                    placeholder="WhatsApp (opcional)"
                    className="border-sales-line"
                  />
                  <button
                    onClick={submitLoggedIn}
                    disabled={join.isPending}
                    className={BUTTON_CLASS}
                    style={{ minHeight: 56 }}
                  >
                    {join.isPending ? "Enviando..." : "Avisar-me quando abrir"}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submitAnonymous}
                  className="mt-5 flex flex-col gap-3"
                >
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome"
                    required
                    maxLength={200}
                    className={INPUT_CLASS}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Seu melhor email"
                    required
                    maxLength={320}
                    className={INPUT_CLASS}
                  />
                  <PhoneField
                    value={phone}
                    onChange={(e164, meta) => {
                      setPhone(e164)
                      setPhoneValid(meta.isValid)
                    }}
                    placeholder="WhatsApp (opcional)"
                    className="border-sales-line"
                  />
                  {/* Honeypot anti-bot: invisível; humano nunca preenche. */}
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute -left-[9999px] h-0 w-0 opacity-0"
                  />
                  <button
                    type="submit"
                    disabled={join.isPending}
                    className={BUTTON_CLASS}
                    style={{ minHeight: 56 }}
                  >
                    {join.isPending
                      ? "Enviando..."
                      : "Entrar na lista de espera"}
                  </button>
                </form>
              )}

              {join.isError && (
                <p className="mt-3 text-[13px] text-red-600">
                  {join.error.message.includes("Muitas tentativas")
                    ? join.error.message
                    : "Não foi possível concluir. Tente novamente."}
                </p>
              )}

              <p className="mt-5 border-t border-sales-line pt-5 text-[11.5px] leading-[1.6] text-sales-text-mid">
                Ao entrar na lista, você concorda em receber emails sobre este
                curso. Sem spam — só o aviso de abertura.
              </p>
            </>
          )}
        </div>
      </div>
    </Shell>
  )
}

// Página de vendas vive fora do grupo (app) — monta o provider localmente
// (queryClient compartilhado de @/lib/trpc/client).
export function WaitlistSection(props: WaitlistSectionProps) {
  return (
    <TRPCProvider>
      <WaitlistSectionInner {...props} />
    </TRPCProvider>
  )
}
